<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
  import { TableHeader, SearchNoResults } from '@/shared/ui';
  import type { PermissionCategory, PermissionMatrixPositionType } from '@/entities/Permission';
  import SaveAndSort from './SaveAndSort.vue';
  import PermissionsMatrixTableRow from './PermissionsMatrixTableRow.vue';

  const INITIAL_VISIBLE_ROWS = 40;
  const ROW_BATCH_SIZE = 30;

  const props = defineProps<{
    category: PermissionCategory;
    hasChanges: boolean;
    isSaving: boolean;
    getPositionPermissionIds: (positionId: number, categoryId: number) => number[];
    positionType: PermissionMatrixPositionType;
    orderOptions?: Array<{ label: string; sort: string; order: string }>;
    showEmptyState?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'toggle-permission', positionId: number, categoryId: number, permissionId: number): void;
    (e: 'save', categoryId: number): void;
    (e: 'update:sort', categoryId: number, sort: string, order: string): void;
  }>();

  const expandedRows = ref<Set<number>>(new Set());
  const visibleRowCount = ref(INITIAL_VISIBLE_ROWS);

  const handlePermissionToggle = (positionId: number, permissionId: number) => {
    emit('toggle-permission', positionId, props.category.id, permissionId);
  };

  const handleSave = () => {
    emit('save', props.category.id);
  };

  const handleSort = (sort: string, order: string) => {
    emit('update:sort', props.category.id, sort, order);
  };

  const toggleRowExpansion = (positionId: number) => {
    if (expandedRows.value.has(positionId)) {
      expandedRows.value.delete(positionId);
    } else {
      expandedRows.value.add(positionId);
    }
  };

  const isRowExpanded = (positionId: number) => {
    return expandedRows.value.has(positionId);
  };

  const handleToggleExpansion = (positionId: number) => {
    toggleRowExpansion(positionId);
  };

  const columnCount = computed(() => {
    return props.category.permissions.length + 3;
  });

  const primaryColumnHeader = computed(() => {
    return props.positionType === 'FUNCTIONAL_ACTIVITY' ? 'Atividades Funcionais' : 'Cargos';
  });

  const visiblePositions = computed(() => {
    return props.category.positions.slice(0, visibleRowCount.value);
  });

  const hasMoreRows = computed(() => {
    return visibleRowCount.value < props.category.positions.length;
  });

  const permissionIdsByPosition = computed(() => {
    const lookup = new Map<number, Set<number>>();

    visiblePositions.value.forEach((position) => {
      lookup.set(
        position.id,
        new Set(props.getPositionPermissionIds(position.id, props.category.id))
      );
    });

    return lookup;
  });

  const loadMoreRows = () => {
    if (!hasMoreRows.value) return;

    visibleRowCount.value = Math.min(
      visibleRowCount.value + ROW_BATCH_SIZE,
      props.category.positions.length
    );
  };

  const handleTableScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (remainingScroll < 240) {
      loadMoreRows();
    }
  };

  const scrollContainerRef = ref<HTMLElement | null>(null);
  const headerWidth = ref<number | null>(null);

  let resizeObserver: ResizeObserver | null = null;
  const instance = getCurrentInstance();

  onMounted(() => {
    const rootElement = instance?.proxy?.$el as HTMLElement | undefined;
    scrollContainerRef.value = rootElement?.querySelector('.overflow-y-auto') ?? null;
    const stickyHeader = rootElement?.querySelector(
      `#header-${props.category.id}`
    ) as HTMLElement | null;

    if (!scrollContainerRef.value) return;

    const syncHeaderWidth = () => {
      if (scrollContainerRef.value) {
        headerWidth.value = scrollContainerRef.value.offsetWidth;
        if (stickyHeader && headerWidth.value !== null) {
          stickyHeader.style.width = `${headerWidth.value}px`;
        }
      }
    };

    syncHeaderWidth();
    if (typeof ResizeObserver === 'undefined') return;

    resizeObserver = new ResizeObserver(syncHeaderWidth);
    resizeObserver.observe(scrollContainerRef.value);
  });

  onUnmounted(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  });
</script>

<template>
  <div class="mt-10">
    <div
      :id="`header-${category.id}`"
      :style="headerWidth !== null ? { width: `${headerWidth}px` } : undefined"
      class="sticky top-0 z-20 bg-white-01 px-6 py-4 flex items-start justify-between border border-b-0 border-gray-02 rounded-t-2xl"
    >
      <div>
        <h3 class="text-base font-semibold">
          {{ category.name }}
        </h3>
      </div>
      <SaveAndSort
        :has-changes="hasChanges"
        :is-saving="isSaving"
        :order-options="orderOptions"
        @save="handleSave"
        @update:sort="handleSort"
      />
    </div>

    <div
      class="max-h-[790px] overflow-x-auto overflow-y-auto border border-t-0 border-gray-02 rounded-2xl rounded-t-none bg-white-01"
      @scroll.passive="handleTableScroll"
    >
      <table class="min-w-full divide-y divide-gray-02">
        <thead>
          <tr>
            <TableHeader
              :text="primaryColumnHeader"
              th-class="sticky top-0 left-0 z-15 bg-white-01 border-b border-gray-02 text-left text-xs font-semibold text-gray-07 pl-6 py-4.5 w-min whitespace-nowrap align-middle"
            />
            <TableHeader
              v-for="permission in category.permissions"
              :key="permission.id"
              :text="permission.description"
              th-class="sticky top-0 z-10 bg-white-01 border-b border-gray-02 text-center text-xs font-semibold text-gray-07 px-2 py-4.5 w-min whitespace-nowrap"
            />
            <TableHeader
              text=""
              th-class="sticky top-0 z-10 bg-white-01 border-b border-gray-02 w-12"
            />
          </tr>
        </thead>
        <tbody class="bg-white-01 divide-y divide-gray-02">
          <tr v-if="showEmptyState">
            <td :colspan="columnCount">
              <SearchNoResults />
            </td>
          </tr>
          <PermissionsMatrixTableRow
            v-for="position in visiblePositions"
            :key="position.id"
            :category="category"
            :position="position"
            :permission-ids="permissionIdsByPosition.get(position.id) ?? new Set()"
            :is-expanded="isRowExpanded(position.id)"
            :column-count="columnCount"
            @toggle-permission="handlePermissionToggle"
            @toggle-expansion="handleToggleExpansion"
          />
          <tr v-if="hasMoreRows">
            <td :colspan="columnCount" class="px-6 py-4 text-center text-xs text-gray-05">
              Role para carregar mais cargos...
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
